import {Dialog, Pane} from 'evergreen-ui';
import React, {memo, useCallback, useEffect, useState} from 'react';
import {BehaviorSubject, Subject} from 'rxjs';

import {useObservable} from 'rxsupport';
import {FatGreenOutlineButton} from './FatGreenOutlineButton';
import {FatAtlantisOutlineButton} from "./FatAtlantisOutlineButton";


function getDomainError(e: any) {
    return e?.graphQLErrors[0]?.extensions?.message;
}

export function useDomainErrorDialog() {
    const [errorMessage$] = useState(() => new BehaviorSubject<string | null>(null));

    // return true if it's a GraphQL domain error
    // otherwise, false, and let clients handle the error
    const tryParsingDomainError = useCallback(
        (e: any) => {
            const domainErrorMessage = getDomainError(e);

            if (!!domainErrorMessage) {
                errorMessage$.next(domainErrorMessage);
                return true;
            }

            return false;
        },
        [errorMessage$]
    );

    return {
        errorMessage$,
        tryParsingDomainError,
    };
}

type Props = {
    message$: Subject<string | null>;
    acceptButtonStyle?: 'green' | 'atlantis';
    closeCallback?: () => void;
};

export const ErrorDialog = memo(function ErrorDialog({message$, acceptButtonStyle = 'green', closeCallback}: Props) {
    const [isShown, setIsShown] = useState(false);

    const message = useObservable(message$, null) || '';

    useEffect(() => {
        const sub = message$.subscribe((msg) => setIsShown(msg != null));
        return () => sub.unsubscribe();
    }, [message$]);

    const onClose = useCallback(() => {
        message$.next(null);
        if (closeCallback) {
            closeCallback();
        }
    }, [closeCallback, message$]);

    return (
        <Dialog
            title='Error'
            hasClose={false}
            intent="danger"
            contentContainerProps={{
                textAlign: 'center'
            }}
            width={510}
            footer={({close}) => (<>
                {/* minus the default evergreen-ui padding in footer container */}
                <Pane width={510 - 16 * 2} className="flex justify-center">
                    <Pane width={120}>
                        {
                            acceptButtonStyle === 'green' &&
                            <FatGreenOutlineButton text="OK" onClick={close}/>
                        }
                        {
                            acceptButtonStyle === 'atlantis' &&
                            <FatAtlantisOutlineButton text="OK" onClick={close}/>
                        }
                    </Pane>
                </Pane>
            </>)}
            isShown={isShown}
            onCloseComplete={onClose}
        >
            {message}
        </Dialog>
    );
});
